<!-- /client/src/components/common/SocialIcons.vue -->
<template>
  <div
    :class="layoutClasses"
    :style="spacingStyle"
  >
    <a
      v-for="(platform, index) in platforms"
      :key="index"
      :href="platform.url"
      class="social-icons group"
      target="_blank"
      rel="noopener noreferrer"
      :aria-label="platform.name"
    >
      <component
        :is="iconMap[platform.name.toLowerCase()] || DefaultIcon"
        :class="['icon', themeClass, `size-${size}`]"
      />
      <span class="sr-only">{{ platform.name }}</span>
    </a>
  </div>
</template>
  
  <script setup>
  import { computed } from 'vue'
  import { Facebook, Instagram, Mail, Youtube } from 'lucide-vue-next'

import AmazonMusic from '@/components/icons/AmazonMusic.vue'
import Apple from '@/components/icons/Apple.vue'
import Bandcamp from '@/components/icons/Bandcamp.vue'
import Discogs from '@/components/icons/Discogs.vue'
import ITunes from '@/components/icons/ITunes.vue'
import RssIcon from '@/components/icons/RssIcon.vue'
import Soundcloud from '@/components/icons/Soundcloud.vue'
import Spotify from '@/components/icons/Spotify.vue'
import TikTok from '@/components/icons/TikTok.vue'
import Tidal from '@/components/icons/Tidal.vue'
import XIcon from '@/components/icons/XIcon.vue'
import YouTubeMusic from '@/components/icons/YouTubeMusic.vue'
const props = defineProps({
  platforms: {
    type: Array,
    default: () => []
  },
  theme: {
    type: String,
    default: 'glow' // or 'outline', 'mono'
  },
  size: {
    type: String,
    default: 'md' // options: 'xs', 'sm', 'md', 'lg', 'xl'
  },
  direction: {
    type: String,
    default: 'horizontal' // or 'vertical'
  },
  spacing: {
    type: [String, Number],
    default: 'default' // string presets ('tight', 'default', 'loose') or any CSS gap value / pixel number
  }
})
  
  const iconMap = {
    instagram: Instagram,
    youtube: Youtube,
    bandcamp: Bandcamp,
    twitter: XIcon, // Legacy support - use local X icon
    x: XIcon, // Current X icon - use local X icon
    facebook: Facebook,
    tiktok: TikTok,
    rss: RssIcon, // Use local RSS icon
    email: Mail,
    spotify: Spotify,
    apple: Apple,
    soundcloud: Soundcloud,
    tidal: Tidal,
    discogs: Discogs,
    // Amazon variants
    amazon: AmazonMusic,
    'amazon music': AmazonMusic,
    // iTunes variants
    itunes: ITunes,
    'itunes store': ITunes,
    // YouTube Music variants
    'youtube music': YouTubeMusic,
    ytmusic: YouTubeMusic,
    'yt music': YouTubeMusic

  }
  
  const DefaultIcon = Mail
  
const spacingPresets = ['tight', 'default', 'loose']

const isPresetSpacing = computed(() => typeof props.spacing === 'string' && spacingPresets.includes(props.spacing))

const layoutClasses = computed(() => {
  const classes = [
    'social-icon-layout',
    props.direction === 'vertical'
      ? 'social-icon-layout--vertical'
      : 'social-icon-layout--horizontal'
  ]

  if (isPresetSpacing.value) {
    classes.push(`social-icon-layout--spacing-${props.spacing}`)
  }

  return classes
})

const resolveCustomSpacing = () => {
  if (typeof props.spacing === 'number') {
    return `${props.spacing}px`
  }

  if (typeof props.spacing === 'string' && !isPresetSpacing.value) {
    // Pure numbers get treated as pixel values
    if (/^\d+(?:\.\d+)?$/.test(props.spacing)) {
      return `${props.spacing}px`
    }

    return props.spacing
  }

  return null
}

const spacingStyle = computed(() => {
  const customGap = resolveCustomSpacing()
  return customGap ? { gap: customGap } : undefined
})
  
  const themeClass = computed(() => {
    switch (props.theme) {
      case 'outline':
        return 'text-navbar-text border border-current p-2'
      case 'mono':
        return 'text-navbar-text opacity-70 hover:opacity-100'
      default: // 'glow'
        return 'text-navbar-text transition-colors'
    }
  })
  </script>
  
  <style scoped>
  .icon {
    color: var(--link-color);
    transition: color 0.2s ease, filter 0.2s ease, transform 0.18s ease;
    will-change: color, filter, transform;
  }
  .social-icon-layout {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0;
  }

  .social-icon-layout--horizontal {
    flex-direction: row;
  }

  @media (min-width: 769px) {
    .social-icon-layout--horizontal {
      flex-wrap: nowrap;
    }
  }

  .social-icon-layout--vertical {
    flex-direction: column;
    align-items: flex-start;
  }

  .social-icon-layout--spacing-tight {
    gap: 0.5rem;
  }

  .social-icon-layout--spacing-default {
    gap: 1rem;
  }

  .social-icon-layout--spacing-loose {
    gap: 2rem;
  }

  .social-icons:hover .icon,
  .icon:hover {
    color: var(--button-bg-color);
    transform: scale(1.05);
    filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.6)) drop-shadow(0 0 16px rgba(255, 255, 255, 0.3));
  }
  .size-xs {
    width: 16px;
    height: 16px;
  }
  .size-sm {
    width: 20px;
    height: 20px;
  }
  .size-md {
    width: 28px;
    height: 28px;
  }
  .size-lg {
    width: clamp(26px, 8vw, 32px);
    height: clamp(26px, 8vw, 32px);
  }
  .size-xl {
    width: clamp(24px, 6vw, 56px);
    height: clamp(24px, 6vw, 56px);
  }

    .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  </style>
